// Pure PostgREST row -> domain mappers for the staff identity backbone.
//
// Extracted from adapters/postgrestStores.ts at ladder step 3 (2026-07-26).
// They live OUTSIDE adapters/ because they are pure: no network, no env, no
// secrets. The package's client-safe `.` entry re-exports them, and it must
// never re-export from a module that also defines a service-role Postgrest
// store — that co-location was what made the barrel's layer boundary
// documentation rather than structure (F5).
//
// Browser components still need these while C3's data layer is inlined in its
// views; ladder step 6/7 moves those reads behind ports, after which only the
// adapters will import this file.

import type { Employee, MessageSource, StaffMessage, Team, TimeEntry, TimeEntrySource } from './domain.js';

export interface EmployeeRow {
  id: string;
  employee_id: string;
  last_name: string;
  first_name: string;
  email?: string | null; // column-granted: absent on staff-client reads (migration 0016)
  phone: string;
  team: Team | null;
  is_senior: boolean;
  contract_start_date?: string | null;
  contract_end_date?: string | null;
  auth_user_id?: string | null;
  nickname?: string | null;
}

export interface StaffMessageRow {
  id: number;
  wa_message_id: string | null;
  employee_id: string | null;
  sender_raw: string;
  body: string;
  has_media: boolean;
  sent_at: string;
  inserted_at: string;
  source: MessageSource;
}

export function rowToEmployee(row: EmployeeRow): Employee {
  return {
    id: row.id,
    employeeId: row.employee_id,
    lastName: row.last_name,
    firstName: row.first_name,
    email: row.email ?? '',
    phone: row.phone,
    team: row.team,
    isSenior: row.is_senior,
    contractStartDate: row.contract_start_date ?? null,
    contractEndDate: row.contract_end_date ?? null,
    authUserId: row.auth_user_id ?? null,
    nickname: row.nickname ?? null,
  };
}

export interface TimeEntryRow {
  id: number;
  employee_id: string;
  clock_in: string;
  clock_out: string | null;
  source: TimeEntrySource;
  updated_by: string | null;
  updated_at: string | null;
}

export function rowToTimeEntry(row: TimeEntryRow): TimeEntry {
  return {
    id: row.id,
    employeeId: row.employee_id,
    clockIn: row.clock_in,
    clockOut: row.clock_out,
    source: row.source,
    updatedBy: row.updated_by,
    updatedAt: row.updated_at,
  };
}

export function rowToStaffMessage(row: StaffMessageRow): StaffMessage {
  return {
    id: row.id,
    waMessageId: row.wa_message_id,
    employeeId: row.employee_id,
    senderRaw: row.sender_raw,
    body: row.body,
    hasMedia: row.has_media,
    sentAt: row.sent_at,
    insertedAt: row.inserted_at,
    source: row.source,
  };
}

/** Shared PostgREST plumbing (service role — the PostgrestChatStore pattern). */
